import { useEffect } from 'react';
import styled from 'styled-components';
import VersionCompPrices from '../models/versionCompPrices.model';
import {
  Text2, Text3, Text3B, Text3M, Text4, Text4B,
  Text5,
  Text5B,
} from '../genericComponents/texts';
import { BlankSpace } from './MarketPriceRange';
import { priceFormat, roundPrice } from '../priceFormat';
import carSolid from '../assets/img/car-solid.svg';
import CardWarning from '../genericComponents/CardWarning';

interface Props {
  vehicle: {
    model: string;
    version: string;
    minPrice: number;
    maxPrice: number;
    autofactPrice: number;
    modelId: number;
  };
  versionCompPrices: VersionCompPrices[];
  setHaveSimilarVersions: (value: boolean) => void;
}

export default function SimilarVersions({ versionCompPrices, vehicle, setHaveSimilarVersions }: Props) {
  const countVersionCompPrices = versionCompPrices.length;
  const { autofactPrice } = vehicle;

  // contar la cantidad de versiones que no tienen precio promedio de publicaciones
  const countNoPublicationsPrices = versionCompPrices.filter((version) => !version.promedio_publicaciones).length;
  // contar la cantidad de versiones que no tienen precio promedio de transferencias
  const countNoTransferencesPrices = versionCompPrices.filter((version) => !version.promedio_transferencias).length;

  // Ordenar versionCompPrices por valor_final de menor a mayor
  const sortedVersionCompPrices = [...versionCompPrices].sort((a, b) => {
    const valorA = a.valor_final || 0;
    const valorB = b.valor_final || 0;
    return valorA - valorB;
  });

  useEffect(() => {
    if (countVersionCompPrices > 1) {
      setHaveSimilarVersions(true);
    }
  }, [countVersionCompPrices]);

  if (countVersionCompPrices <= 1) {
    return <CardWithoutInfo model={vehicle.model} />;
  }

  return (
    <Container>
      <CardWarning text="Los precios de esta sección son referenciales y no cuentan con ajustes de precio por región geográfica ni kilometraje." />
      <BlankSpace height="10px" />

      {countNoPublicationsPrices === countVersionCompPrices && countNoPublicationsPrices !== countNoTransferencesPrices ? <CardWarning text="No se encontró información de publicaciones activas para el modelo consultado." /> : ''}

      {countNoTransferencesPrices === countVersionCompPrices && countNoTransferencesPrices !== countNoPublicationsPrices ? <CardWarning text="No se encontró información de ventas reales para el modelo consultado." /> : ''}

      {countNoPublicationsPrices === countVersionCompPrices && countNoPublicationsPrices === countNoTransferencesPrices ? <CardWarning text="Para el modelo tasado en este informe no se encontraron publicaciones de venta activas ni información de ventas reales." /> : ''}

      <TableContainer>
        <Table>
          <thead>
            <tr>
              <Th><Text4B>Versión similar</Text4B></Th>
              <Th><Text4B>Promedio publicaciones</Text4B></Th>
              <Th><Text4B>Promedio ventas reales</Text4B></Th>
              <Th><Text4B>Precio recomendado venta</Text4B></Th>
            </tr>
          </thead>
          <tbody>
            {sortedVersionCompPrices.map((version, idx) => {
              const rowBg = idx % 2 === 0 ? '#fff' : '#F6F6F6';
              const isSimilarVersion = version.modelo_id === vehicle.modelId;
              const rowBorder = isSimilarVersion ? '1px solid #8FABC3' : 'none';
              let showTransferencesPrice = false;
              if (version.promedio_transferencias) {
                const diff = version.promedio_transferencias > autofactPrice ? version.promedio_transferencias - autofactPrice : autofactPrice - version.promedio_transferencias;
                if (version.promedio_transferencias > vehicle.minPrice && version.promedio_transferencias < vehicle.maxPrice) {
                  showTransferencesPrice = true;
                } else if (diff < 500000) {
                  showTransferencesPrice = true;
                } else {
                  showTransferencesPrice = false;
                }
              }
              return (
                <tr key={version.version_comp} style={{ background: rowBg, border: rowBorder }}>
                  <Td>
                    {isSimilarVersion ? (
                      <Text3B>
                        {version.modelo_comp}
                        {' '}
                        <Text2>
                          <img src={carSolid} width={10} height={10} alt="" />
                          {' '}
                          Versión consultada
                        </Text2>
                      </Text3B>
                    ) : (
                      <Text3B>{version.modelo_comp}</Text3B>
                    )}
                    <BlankSpace height="1px" />
                    <Text3B>{version.version_comp}</Text3B>
                  </Td>
                  <TdCenter><Text4>{`${version.promedio_publicaciones ? `$${priceFormat(roundPrice(version.promedio_publicaciones, 5))}` : '-'}`}</Text4></TdCenter>
                  <TdCenter><Text4>{`${(showTransferencesPrice || !isSimilarVersion) && version.promedio_transferencias ? `$${priceFormat(roundPrice(version.promedio_transferencias, 5))}` : '-'}`}</Text4></TdCenter>
                  <TdCenter><Text4B>{`${version.valor_final ? `$${priceFormat(roundPrice(version.valor_final, 5))}` : '-'}`}</Text4B></TdCenter>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>
      <Glossary>
        <Text3M>¿Necesitas ayuda para identificar la versión?</Text3M>
        <Text3> En este glosario encontrarás los principales equipamientos:</Text3>
        <BlankSpace height="10px" />
        <SubCont9>
          <Text2>
            <ul>
              <li>
                <Text3M>MT: </Text3M>
                Transmisión mecánica.
              </li>
              <li>
                <Text3M>AT: </Text3M>
                Transmisión automática.
              </li>
              <li>
                <Text3M>CVT: </Text3M>
                Transmisión continua variable.
              </li>
              <li>
                <Text3M>3P/5P: </Text3M>
                N° de puertas
              </li>
              <li>
                <Text3M>SR: </Text3M>
                Sunroof
              </li>
              <li>
                <Text3M>AB/2AB/DAB: </Text3M>
                Airbags
              </li>
            </ul>
          </Text2>
          <Text2>
            <ul>
              <li>
                <Text3M>LL: </Text3M>
                Llantas
              </li>
              <li>
                <Text3M>Full: </Text3M>
                Full equipo
              </li>
              <li>
                <Text3M>NB: </Text3M>
                Notchback
              </li>
              <li>
                <Text3M>SW: </Text3M>
                Station Wagon
              </li>
              <li>
                <Text3M>DC: </Text3M>
                Doble cabina
              </li>
              <li>
                <Text3M>CLIM: </Text3M>
                Climatizador
              </li>
            </ul>
          </Text2>
          <Text2>
            <ul>
              <li>
                <Text3M>PL: </Text3M>
                Puerta lateral
              </li>
              <li>
                <Text3M>ABS: </Text3M>
                Frenos ABS
              </li>
              <li>
                <Text3M>AC/AA: </Text3M>
                Aire acondicionado
              </li>
              <li>
                <Text3M>DH/DA: </Text3M>
                Dirección hidráulica/asistida
              </li>
              <li>
                <Text3M>CD/MP3/DVD: </Text3M>
                Reproductor CD / MP3 / DVD
              </li>
              <li>
                <Text3M>EE: </Text3M>
                Equipamiento eléctrico
              </li>
            </ul>
          </Text2>
          <Text2>
            <ul>
              <li>
                <Text3M>HB: </Text3M>
                Hatchback
              </li>
              <li>
                <Text3M>SDN: </Text3M>
                Sedán
              </li>
              <li>
                <Text3M>CS: </Text3M>
                Cabina simple
              </li>
              <li>
                <Text3M>CC: </Text3M>
                Cabriolet Coupé
              </li>
              <li>
                <Text3M>CUE/LTH: </Text3M>
                Tapiz Cuero
              </li>
            </ul>
          </Text2>
        </SubCont9>
      </Glossary>
    </Container>
  );
}

function CardWithoutInfo({ model }: { model: string }) {
  return (
    <Container>
      <SubCont4 style={{ alignItems: 'center', textAlign: 'center', marginTop: '40px' }}>
        <Text5B style={{ color: '#0B3254', fontSize: '20px', marginBottom: '10px' }}>
          El modelo
          {' '}
          {model}
          {' '}
          cuenta con versión única.
        </Text5B>
        <Text5 style={{ color: '#3D5176', fontSize: '18px', marginBottom: '30px' }}>
          Algunos modelos y versiones de años sólo se comercializan con una versión.
        </Text5>
      </SubCont4>
    </Container>
  );
}

const SubCont4 = styled.div`
  display: flex;
  flex-direction: column;
  @media (max-width: 700px){
    flex: 1;
  }
`;

const Container = styled.div`
  padding: 10px 0;
  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

const TableContainer = styled.div`
  margin: 20px 0px;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background: #F6F6F6;
  padding: 15px;
  text-align: left;
  color: #0B3254;
  font-weight: 700;
`;

const Td = styled.td`
  padding: 5px;
`;

const TdCenter = styled.td`
  padding: 5px;
  text-align: center;
`;

const Glossary = styled.div`
  margin: 10px;
  border: 1px solid #EBF1F5;
  border-radius: 10px;
  padding: 10px;
  width: 96%;
  background: #EBF1F5;
  border: 1px solid #8FABC3;
  @media (max-width: 700px){
    width: auto;
  }
`;
const SubCont9 = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  width: 100%;
  @media (max-width: 700px){
    flex-direction: column;
    justify-content: center;
    padding-left: 20px;
  }
`;
